import { useState, useEffect } from 'react';
import { storage } from '@/lib/storage';
import { cn } from '@/lib/utils';

/**
 * Hook to fetch protected images with authorization token
 * Returns a blob URL that can be used in img tags
 */
export function useProtectedImage(url: string | null | undefined): {
  imageSrc: string | null;
  isLoading: boolean;
  error: Error | null;
} {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!url) {
      setImageSrc(null);
      return;
    }

    // Check if it's a protected file URL
    const isProtectedUrl = url.includes('/api/v1/files/');
    
    if (!isProtectedUrl) {
      // Not a protected URL, use as-is
      setImageSrc(url);
      return;
    }

    const token = storage.getToken();
    if (!token) {
      // No token available, try loading without auth (might fail)
      setImageSrc(url);
      return;
    }

    // Convert full URL to relative URL to use Vite proxy and avoid CORS
    let fetchUrl = url;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      // Extract the path part: /api/v1/files/...
      const urlObj = new URL(url);
      fetchUrl = urlObj.pathname;
    }

    let objectUrl: string | null = null;

    const fetchImage = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(fetchUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load image: ${response.status}`);
        }

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        setImageSrc(objectUrl);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load image'));
        // Fallback to placeholder
        setImageSrc(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchImage();

    // Cleanup: revoke object URL when component unmounts or URL changes
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [url]);

  return { imageSrc, isLoading, error };
}

/**
 * Component for displaying protected images
 */

interface ProtectedImageProps {
  src: string | null | undefined;
  alt: string;
  className?: string;
  containerClassName?: string;
  placeholder?: React.ReactNode;
}

export function ProtectedImage({
  src,
  alt,
  className,
  containerClassName,
  placeholder,
}: ProtectedImageProps) {
  const { imageSrc, isLoading, error } = useProtectedImage(src);

  // Placeholder SVG
  const defaultPlaceholder = (
    <div className="w-full h-full bg-slate-100 flex items-center justify-center">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-slate-400"
      >
        <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
        <circle cx="9" cy="9" r="2" />
        <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
      </svg>
    </div>
  );

  return (
    <div className={cn('relative overflow-hidden', containerClassName)}>
      {isLoading || !imageSrc || error ? (
        placeholder || defaultPlaceholder
      ) : (
        <img
          src={imageSrc}
          alt={alt}
          className={className}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            if (!target.dataset.error) {
              target.dataset.error = 'true';
              target.style.display = 'none';
            }
          }}
        />
      )}
    </div>
  );
}
